// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  advisoryPositionsAdapter,
  advisoryBalancesHistoricalAdapter,
  advisoryTransactionsAdapter,
  advisoryAccountsDebtAdapter,
  initialState,
} from './advisory-repository-state';
import type {
  AdvisoryPositionsState,
  AdvisoryBalancesHistoricalState,
  AdvisoryTransactionsState,
  AdvisoryAccountsDebtState,
} from '@shared/data-access/interfaces/redux/advisory-repository-state-types';

export const advisoryRepositorySlice = createSlice({
  initialState: () => initialState,
  name: 'advisoryRepository',
  reducers: {
    reset: () => initialState,

    /**
     * Reset cache entries by client ID only.
     *
     * Removes all cached positions, balances historical, transactions, and accounts
     * debt that match the specified client_id, regardless of institution.
     *
     * @param payload - Object with clientId
     */
    resetByClientId: (
      state,
      { payload: { clientId } }: PayloadAction<{ clientId: number }>,
    ) => {
      const positions = advisoryPositionsAdapter
        .getSelectors()
        .selectAll(state.positions);

      const balancesHistorical = advisoryBalancesHistoricalAdapter
        .getSelectors()
        .selectAll(state.balancesHistorical);

      const transactions = advisoryTransactionsAdapter
        .getSelectors()
        .selectAll(state.transactions);

      const accountsDebt = advisoryAccountsDebtAdapter
        .getSelectors()
        .selectAll(state.accountsDebt);

      const positionsToRemove = positions
        .filter(position => position.client_id === clientId)
        .map(position => position.id);

      const balancesHistoricalToRemove = balancesHistorical
        .filter(balance => balance.client_id === clientId)
        .map(balance => balance.id);

      const transactionsToRemove = transactions
        .filter(transaction => transaction.client_id === clientId)
        .map(transaction => transaction.id);

      const accountsDebtToRemove = accountsDebt
        .filter(entry => entry.client_id === clientId)
        .map(entry => entry.id);

      advisoryPositionsAdapter.removeMany(state.positions, positionsToRemove);

      advisoryBalancesHistoricalAdapter.removeMany(
        state.balancesHistorical,
        balancesHistoricalToRemove,
      );

      advisoryTransactionsAdapter.removeMany(
        state.transactions,
        transactionsToRemove,
      );

      advisoryAccountsDebtAdapter.removeMany(
        state.accountsDebt,
        accountsDebtToRemove,
      );
    },

    /**
     * Reset cache entries by client ID and institution IDs.
     *
     * Removes all cached positions, balances historical, transactions, and accounts
     * debt that match both the specified client_id and any of the specified
     * institution IDs.
     *
     * @param payload - Object with clientId and institutions array
     */
    resetByClientAndInstitutionId: (
      state,
      {
        payload: { clientId, institutions },
      }: PayloadAction<{ clientId: number; institutions: string[] }>,
    ) => {
      const positions = advisoryPositionsAdapter
        .getSelectors()
        .selectAll(state.positions);

      const balancesHistorical = advisoryBalancesHistoricalAdapter
        .getSelectors()
        .selectAll(state.balancesHistorical);

      const transactions = advisoryTransactionsAdapter
        .getSelectors()
        .selectAll(state.transactions);

      const accountsDebt = advisoryAccountsDebtAdapter
        .getSelectors()
        .selectAll(state.accountsDebt);

      const positionsToRemove = positions
        .filter(
          position =>
            institutions.includes(position.institution_id) &&
            position.client_id === clientId,
        )
        .map(position => position.id);

      const balancesHistoricalToRemove = balancesHistorical
        .filter(
          balance =>
            institutions.includes(balance.institution_id) &&
            balance.client_id === clientId,
        )
        .map(balance => balance.id);

      const transactionsToRemove = transactions
        .filter(
          transaction =>
            institutions.includes(transaction.institution_id) &&
            transaction.client_id === clientId,
        )
        .map(transaction => transaction.id);

      const accountsDebtToRemove = accountsDebt
        .filter(
          entry =>
            institutions.includes(entry.institution_id) &&
            entry.client_id === clientId,
        )
        .map(entry => entry.id);

      advisoryPositionsAdapter.removeMany(state.positions, positionsToRemove);

      advisoryBalancesHistoricalAdapter.removeMany(
        state.balancesHistorical,
        balancesHistoricalToRemove,
      );

      advisoryTransactionsAdapter.removeMany(
        state.transactions,
        transactionsToRemove,
      );

      advisoryAccountsDebtAdapter.removeMany(
        state.accountsDebt,
        accountsDebtToRemove,
      );
    },

    upsertAdvisoryPositions: (
      state,
      { payload }: PayloadAction<Array<AdvisoryPositionsState>>,
    ) => {
      advisoryPositionsAdapter.upsertMany(state.positions, payload);
    },

    upsertAdvisoryBalancesHistorical: (
      state,
      { payload }: PayloadAction<Array<AdvisoryBalancesHistoricalState>>,
    ) => {
      advisoryBalancesHistoricalAdapter.upsertMany(
        state.balancesHistorical,
        payload,
      );
    },

    upsertAdvisoryTransactions: (
      state,
      { payload }: PayloadAction<Array<AdvisoryTransactionsState>>,
    ) => {
      advisoryTransactionsAdapter.upsertMany(state.transactions, payload);
    },

    upsertAdvisoryAccountsDebt: (
      state,
      { payload }: PayloadAction<Array<AdvisoryAccountsDebtState>>,
    ) => {
      advisoryAccountsDebtAdapter.upsertMany(state.accountsDebt, payload);
    },
  },
  extraReducers: () => {
    return;
  },
});
